import {
  Flex,
  Text,
  SearchInput,
  Tab,
  Tabs,
  TabList,
  useColorStyle,
  useColorMode,
} from '@tonic-one/react';
import MenuWithLabel from '@Components/Common/Menu';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { DetailsTable } from './table';
import {
  FeedbackFilterCategory,
  FeedbackFilterStatus,
  FilterKeywordField,
  FilterKeyword,
  ActionModalState,
  EuqTimeRangeEnum,
  FeedbackResultCategory,
} from '../../type';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { FeedbackCategoryStrMap } from '../../constants';
import AddFilterMenu from '../addFilterMenu';
import { DownloadBtn, ApproveBtn, RejectBtn } from '../actionBtn';
import ActionModal from '@Components/Common/ActionModal';
import { FILTER_DEBOUNCE_DELAY } from '../constants';
import {
  useEuqFeedbackQuery,
  useManualApproveConfigQuery,
  useFilters,
  useEuqFeedbackMutations,
} from '../hooks';

const ReportedEmailDetails = ({
  timeRange,
}: {
  timeRange: EuqTimeRangeEnum;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { orgId } = useOrgStore();
  const { t } = useTranslation();

  // Track previous orgId to only reset filters when it actually changes
  const prevOrgIdRef = useRef<string>();

  const { data: manualApproveConfig } = useManualApproveConfigQuery(orgId);

  const initialStatus = manualApproveConfig
    ? FeedbackFilterStatus.Created
    : FeedbackFilterStatus.Approved;

  const {
    status,
    category,
    keyword,
    sortKey,
    setStatus,
    setCategory,
    setSortKey,
    updateKeyword,
    updateKeywordSubject,
    resetFilters,
  } = useFilters({
    initialStatus,
    timeRange,
  });

  const [debouncedKeyword, setDebouncedKeyword] =
    useState<FilterKeyword>(keyword);

  // Debounce the keyword updates for the query
  const debouncedSetKeyword = useMemo(
    () =>
      debounce((newKeyword: FilterKeyword) => {
        setDebouncedKeyword(newKeyword);
      }, FILTER_DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    debouncedSetKeyword(keyword);
  }, [keyword, debouncedSetKeyword]);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const resetPagination = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  // Data query
  const {
    data,
    isLoading: loading,
    error,
  } = useEuqFeedbackQuery({
    status,
    category,
    resultCategory: FeedbackResultCategory.All,
    requestSource: 'all',
    timeRange,
    keyword: debouncedKeyword, // Use debounced keyword for the query
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sortKey,
    orgId,
    mitigationStatus: null,
  });

  const { contentList, total } = useMemo(
    () => ({
      contentList: data?.contentList || [],
      total: data?.total || 0,
    }),
    [data]
  );

  const [rowSelection, setRowSelection] = useState<{
    [key: string]: boolean;
  }>({});
  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );

  const [actionModalState, setActionModalState] = useState<ActionModalState>({
    name: null,
    id: null,
  });
  const resetActionModalState = useCallback(() => {
    setActionModalState({
      name: null,
      id: null,
    });
  }, []);

  const { approveMutation, rejectMutation } = useEuqFeedbackMutations({
    orgId,
    onSuccess: () => {
      setRowSelection({});
      resetPagination();
    },
  });

  const categoryMenuItems = useMemo(() => {
    return [
      FeedbackFilterCategory.All,
      FeedbackFilterCategory.Spam,
      FeedbackFilterCategory.Phishing,
      FeedbackFilterCategory.NotSpam,
    ].map((item) => {
      return {
        key: item,
        value: t(FeedbackCategoryStrMap[item]),
      };
    });
  }, [t]);

  // Status tabs
  const statusArr = useMemo(
    () => [
      FeedbackFilterStatus.Created,
      FeedbackFilterStatus.Approved,
      FeedbackFilterStatus.Rejected,
      FeedbackFilterStatus.Bypassed,
    ],
    []
  );

  // Reset filters only when orgId actually changes (not on initial load)
  useEffect(() => {
    if (prevOrgIdRef.current && prevOrgIdRef.current !== orgId) {
      resetFilters();
      resetPagination();
      setRowSelection({});
    }
    prevOrgIdRef.current = orgId;
  }, [orgId, resetFilters, resetPagination]);

  // Reset pagination when timeRange changes (but keep filters)
  useEffect(() => {
    resetPagination();
    setRowSelection({});
  }, [timeRange, resetPagination]);

  // Helper function to reset selections and pagination
  const resetSelectionsAndPagination = useCallback(() => {
    setRowSelection({});
    resetPagination();
  }, [resetPagination]);

  // Handle error
  if (error) {
    return <Text color="red">{t('common.apiException.internalError')}</Text>;
  }

  return (
    <>
      <Flex direction="column" flex="auto" gap="8px" mt="4x">
        <Text fontWeight="600" fontSize="20px" lineHeight="28px" pl="16px">
          {t('operations.euq.detail.title')}
        </Text>
        <Flex pl="16px" gap="8px">
          <MenuWithLabel
            menuItems={categoryMenuItems}
            minWidth={200}
            onClick={(e: any) => {
              resetSelectionsAndPagination();
              setCategory(e.target.value as FeedbackFilterCategory);
            }}
            selectedKey={category}
            label={t('operations.euq.label.reportedAs')}
          />
          <SearchInput
            width="200px"
            placeholder={t('operations.euq.detail.table.header.subject')}
            value={keyword.subject}
            onChange={(e: any) => {
              const value = e.target.value;
              setRowSelection({});
              resetPagination();
              updateKeywordSubject(value);
            }}
            onClearInput={() => {
              setRowSelection({});
              resetPagination();
              updateKeywordSubject('');
            }}
          />

          <AddFilterMenu
            keyword={keyword}
            setKeyword={(field: FilterKeywordField, value: string) => {
              resetSelectionsAndPagination();
              updateKeyword(field, value);
            }}
          />

          <DownloadBtn selectedIds={selectedIds} />

          {status === FeedbackFilterStatus.Created && (
            <>
              <ApproveBtn
                selectedIds={selectedIds}
                refreshData={() => {
                  setRowSelection({});
                  resetPagination();
                }}
              />
              <RejectBtn
                selectedIds={selectedIds}
                refreshData={() => {
                  setRowSelection({});
                  resetPagination();
                }}
              />
            </>
          )}
        </Flex>
        <Flex direction="column" flex="auto">
          <Tabs
            index={statusArr.indexOf(status)}
            onChange={(index: number) => {
              resetSelectionsAndPagination();
              setStatus(statusArr[index]);
            }}
          >
            <TabList>
              {statusArr.map((v) => (
                <Tab key={v}>{t(`operations.euq.statusTab.${v}`)}</Tab>
              ))}
            </TabList>
          </Tabs>
          <Flex
            borderTop="2px solid"
            borderTopColor={colorStyle.divider}
            minHeight={425}
            flex={1}
          >
            <DetailsTable
              data={contentList}
              sortKey={sortKey}
              setSortKey={(v: string) => {
                resetSelectionsAndPagination();
                setSortKey(v);
              }}
              actionType={status}
              setActionModalState={setActionModalState}
              enableSelection={true}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              isLoading={loading}
              pagination={pagination}
              onPaginationChange={setPagination}
              rowCount={total}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </Flex>
        </Flex>
      </Flex>
      <RejectAndApproveModal
        actionModalState={actionModalState}
        resetActionModalState={resetActionModalState}
        approveMutation={approveMutation}
        rejectMutation={rejectMutation}
      />
    </>
  );
};

export default ReportedEmailDetails;

const RejectAndApproveModal = ({
  actionModalState,
  resetActionModalState,
  approveMutation,
  rejectMutation,
}: {
  actionModalState: ActionModalState;
  resetActionModalState: () => void;
  approveMutation: any;
  rejectMutation: any;
}) => {
  const { t } = useTranslation();

  const onReject = async () => {
    try {
      await rejectMutation.mutateAsync([actionModalState.id as string]);
    } finally {
      resetActionModalState();
    }
  };

  const onApprove = async () => {
    try {
      await approveMutation.mutateAsync([actionModalState.id as string]);
    } finally {
      resetActionModalState();
    }
  };

  return (
    <>
      <ActionModal
        isOpen={actionModalState.name === 'approve'}
        onClose={resetActionModalState}
        title={t('operations.euq.action.approve.title')}
        body={t('operations.euq.action.approve.desc')}
        confirmText={t('common.action.label.approve')}
        onConfirm={onApprove}
      />
      <ActionModal
        isOpen={actionModalState.name === 'reject'}
        onClose={resetActionModalState}
        title={t('operations.euq.action.reject.title')}
        body={t('operations.euq.action.reject.desc')}
        confirmText={t('common.action.label.reject')}
        onConfirm={onReject}
      />
    </>
  );
};
